import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import {
  StaticPageApiUrls,
  ExamInfoApiUrls,
} from "../../Api/Auth/ApiUrls";
import { noAuthfetcher } from "../../Api/swrConfig";
import HeroSection from "../../Components/Common/HeroSection";
import { Loader } from "../../Components/Common/Loader";
import NotFound from "../Notfound";
import { useContact } from "../../Context/ContactContext";

const DEFAULT_BACKGROUND =
  "https://storage.googleapis.com/road-to-fe/uploads/1741417535659-07d447cd-82a2-4b1b-8c08-9fffeaaefa18-colleagues-doing-team-work-project_23-2149361607.avif";

const StaticPage = () => {
  const { slug } = useParams();
  const { footerPages } = useContact();

  const isFooterSlug = useMemo(() => {
    if (!slug || !Array.isArray(footerPages)) return false;
    return footerPages.some((item) => item.slug === slug);
  }, [footerPages, slug]);

  const {
    data,
    error,
    isLoading,
  } = useSWR(
    !isFooterSlug && slug ? StaticPageApiUrls.info(slug) : null,
    noAuthfetcher
  );

  const pageData = data?.data;
  const contentParsed = useMemo(() => {
    if (!pageData?.content) return {};
    try {
      return typeof pageData.content === "string"
        ? JSON.parse(pageData.content)
        : pageData.content;
    } catch (parseError) {
      console.warn("Failed to parse static page content", parseError);
      return {};
    }
  }, [pageData]);

  const descriptionHtml = useMemo(() => {
    if (!pageData) return "";
    const raw = pageData.content;
    if (!raw) return pageData.bodyDescription || "";
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return (
        
        parsed?.bodyContent ||
        
        ""
      );
    } catch (parseError) {
      return pageData.bodyDescription || "";
    }
  }, [pageData]);

  const shouldFetchExamInfo = isFooterSlug || error?.response?.status === 404;

  const {
    data: examData,
    error: examError,
    isLoading: isExamLoading,
  } = useSWR(
    shouldFetchExamInfo && slug ? ExamInfoApiUrls.single(slug) : null,
    noAuthfetcher
  );

  if (error && !shouldFetchExamInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">
          Something went wrong while loading this page.
        </p>
      </div>
    );
  }

  if (isLoading || (shouldFetchExamInfo && isExamLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (shouldFetchExamInfo) {
    if (examError || !examData?.data) {
      return <NotFound />;
    }

    const examInfo = examData.data;
    return (
      <div className="pb-12">
        <HeroSection
          title={examInfo.title}
          backgroundImage={DEFAULT_BACKGROUND}
          subtitle={examInfo.subtitle || "Exam Information"}
        />
        <div className="container mx-auto mt-10 px-4">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div
              className={`prose max-w-none ${examInfo.featuredImage ? "flex-1" : "w-full"}`}
              dangerouslySetInnerHTML={{ __html: examInfo.description || "" }}
            />
            {examInfo.featuredImage && (
              <div className="lg:w-1/3 w-full flex-shrink-0">
                <img
                  src={examInfo.featuredImage}
                  alt={examInfo.title}
                  className="w-full h-auto rounded-lg shadow-md object-cover"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!pageData) {
    return <NotFound />;
  }

  const heroTitle = pageData.title || contentParsed?.heroTitle || "";
  const heroSubtitle =
    pageData.headerDescription || contentParsed?.heroSubtitle || "";
  const backgroundImage =
    contentParsed?.heroImage ||
    contentParsed?.backgroundImage ||
    pageData.image ||
    DEFAULT_BACKGROUND;
  const bodyHtml =
    contentParsed?.content ||
    contentParsed?.bodyContent ||
    pageData.bodyDescription ||
    descriptionHtml;

  return (
    <div className="pb-12">
      <HeroSection
        title={heroTitle}
        backgroundImage={backgroundImage}
        subtitle={heroSubtitle}
      />
      <div className="container mx-auto mt-10 px-4">
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div
            className={`prose max-w-none ${pageData.featuredImage ? "flex-1" : "w-full"}`}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
          {pageData.featuredImage && (
            <div className="lg:w-1/3 w-full flex-shrink-0">
              <img
                src={pageData.featuredImage}
                alt={heroTitle}
                className="w-full h-auto rounded-lg shadow-md object-cover"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaticPage;

